<script setup lang="ts">
// The real feed card with real vacancies, playable by guests: swipe, like, and
// a sign-up sheet instead of the response flow.
import { useElementVisibility, useThrottleFn } from '@vueuse/core'
import VacancyView from '~/components/feed/VacancyView.vue'
import { Button } from '~/components/ui/button'
import type { FeedVacancyItem } from '~/entities/feed'
import LandingMockRecorder from './LandingMockRecorder.vue'

const props = defineProps<{ items: FeedVacancyItem[] }>()
const emit = defineEmits<{ swipe: [count: number] }>()

const root = useTemplateRef<HTMLElement>('root')
const visible = useElementVisibility(root, { threshold: 0.4 })
const index = defineModel<number>('index', { default: 0 })
const direction = ref<1 | -1>(1)
const swipes = ref(0)
const liked = ref(new Set<string>())
const burst = ref(0)
const sheet = ref(false)

const current = computed(() => props.items[index.value] ?? null)

function show(next: number, step: 1 | -1) {
  if (props.items.length < 2 || next === index.value) return
  direction.value = step
  index.value = next
  sheet.value = false
  swipes.value++
  emit('swipe', swipes.value)
}

function go(step: 1 | -1) {
  show((index.value + step + props.items.length) % props.items.length, step)
}

function jump(next: number) {
  show(next, next > index.value ? 1 : -1)
}

const onWheel = useThrottleFn((event: WheelEvent) => {
  if (Math.abs(event.deltaY) > 20) go(event.deltaY > 0 ? 1 : -1)
}, 700, false, true)

let touchStart: number | null = null
function onTouchEnd(event: TouchEvent) {
  const end = event.changedTouches[0]?.clientY
  if (touchStart !== null && end !== undefined && Math.abs(end - touchStart) > 40) go(end < touchStart ? 1 : -1)
  touchStart = null
}

function onAction(action: 'primary' | 'favorite' | 'more') {
  if (!current.value) return
  if (action === 'favorite') {
    const next = new Set(liked.value)
    if (next.has(current.value.id)) next.delete(current.value.id)
    else {
      next.add(current.value.id)
      burst.value++
    }
    liked.value = next
    return
  }
  sheet.value = true
}

defineExpose({ go, jump })
</script>

<template>
  <div
    ref="root"
    tabindex="0"
    aria-roledescription="лента вакансий"
    class="relative size-full touch-none overflow-hidden bg-black outline-none"
    @wheel.prevent="onWheel"
    @touchstart.passive="touchStart = $event.touches[0]?.clientY ?? null"
    @touchend="onTouchEnd"
    @keydown.down.prevent="go(1)"
    @keydown.up.prevent="go(-1)"
  >
    <LandingMockRecorder v-if="!current" />
    <Transition
      v-else
      :enter-from-class="direction === 1 ? 'translate-y-full' : '-translate-y-full'"
      :leave-to-class="direction === 1 ? '-translate-y-full' : 'translate-y-full'"
      enter-active-class="transition-transform duration-500 ease-out"
      leave-active-class="transition-transform duration-500 ease-out"
    >
      <VacancyView
        :key="current.id"
        :item="current"
        :active="visible && !sheet"
        :favorite-active="liked.has(current.id)"
        sheet
        class="absolute inset-0"
        @finished="go(1)"
        @action="onAction"
      />
    </Transition>

    <Icon
      v-if="burst"
      :key="burst"
      name="tabler:heart-filled"
      class="landing-heart-burst pointer-events-none absolute left-1/2 top-[40%] z-40 size-28 text-destructive drop-shadow-xl"
      aria-hidden="true"
    />

    <Transition enter-from-class="translate-y-full" leave-to-class="translate-y-full" enter-active-class="transition-transform duration-300" leave-active-class="transition-transform duration-300">
      <div v-if="sheet" class="absolute inset-x-0 bottom-0 z-50 rounded-t-3xl bg-background p-5 text-foreground shadow-2xl">
        <div class="mx-auto mb-4 h-1 w-10 rounded-full bg-muted" />
        <p class="text-lg font-semibold leading-tight">Понравилась вакансия?</p>
        <p class="mt-1 text-sm text-muted-foreground">Зарегистрируйся за минуту — и откликайся в один тап.</p>
        <Button as-child shape="pill" class="mt-4 w-full">
          <NuxtLink to="/signup/applicant">Зарегистрироваться</NuxtLink>
        </Button>
        <Button variant="ghost" shape="pill" class="mt-1 w-full" @click="sheet = false">Смотреть дальше</Button>
      </div>
    </Transition>

    <div v-if="items.length > 1 && swipes === 0" class="pointer-events-none absolute inset-x-0 top-1/3 z-30 flex justify-center">
      <span class="flex animate-bounce items-center gap-1.5 rounded-full bg-black/60 px-3 py-1.5 text-xs font-medium text-white backdrop-blur">
        <Icon name="tabler:hand-finger" class="size-4" />
        Листай вверх
      </span>
    </div>
  </div>
</template>

<style scoped>
.landing-heart-burst {
  animation: landing-heart-burst 0.8s ease-out forwards;
}

@keyframes landing-heart-burst {
  0% { transform: translate(-50%, -50%) scale(0.2); opacity: 0; }
  30% { transform: translate(-50%, -50%) scale(1.15); opacity: 1; }
  60% { transform: translate(-50%, -50%) scale(0.95); opacity: 1; }
  100% { transform: translate(-50%, -80%) scale(1.1); opacity: 0; }
}
</style>
